/* eslint-disable jsx-a11y/click-events-have-key-events */

"use client";

import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useTable } from "react-table";
import {
  MdCancel,
  MdCheck,
  MdOutlinePictureAsPdf,
  MdAccessTime,
  MdTrendingUp,
  MdTrendingDown,
} from "react-icons/md";
import {
  FiCalendar,
  FiUser,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
  FiFileText,
  FiLayers,
  FiGrid,
} from "react-icons/fi";
import { PageTitle } from "../../components/PageTitle";
import { api } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

type ViewMode = "general" | "byZone" | "emergency";

// Função para formatar data de forma mais robusta
function formatDate(dateString: string): string {
  try {
    if (!dateString) return "N/A";

    // Tenta diferentes formatos de data
    let date: Date;

    // Se já é uma string ISO válida
    if (dateString.includes("T") && dateString.includes("Z")) {
      date = new Date(dateString);
    } else {
      // Tenta outros formatos
      date = new Date(dateString);
    }

    // Verifica se a data é válida
    if (Number.isNaN(date.getTime())) {
      // eslint-disable-next-line no-console
      console.warn("Data inválida recebida:", dateString);
      return "Data inválida";
    }

    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Erro ao formatar data:", error, dateString);
    return "Erro na data";
  }
}

interface ListCostumerMonitoring {
  report_id: string;
  costumer_name: string;
  services: string;
  last_report: string;
  alert_days_without_report: number;
  days_without_report: number;
  alert: boolean;
  zone_id: string | null;
  zone_name: string | null;
  emergency: boolean;
}

interface ZoneInfo {
  id: string;
  name: string;
}

interface CostumersByZone {
  zone: ZoneInfo | null;
  costumers: ListCostumerMonitoring[];
}

interface BigNumbersMonitoring {
  total_true: number;
  total_false: number;
}

function ReportCell({ value }: { value: string }) {
  return (
    <div className="flex justify-center">
      <Link
        to={`/tech-visit-reports/pdf/${value}`}
        target="_blank"
        className="group/pdf relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#0085BD] to-[#006a96] hover:from-[#006a96] hover:to-[#005580] text-white text-xs font-medium shadow-sm hover:shadow-md hover:scale-105 active:scale-95 transition-all duration-200"
      >
        <MdOutlinePictureAsPdf size={14} />
        <span className="hidden sm:inline">Ver PDF</span>
        <div className="absolute inset-0 rounded-lg bg-white/0 group-hover/pdf:bg-white/10 transition-colors duration-200" />
      </Link>
    </div>
  );
}

function SituacaoCell({ value }: { value: boolean }) {
  return value === true ? (
    <span className="group/badge relative inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200 cursor-default">
      <span className="flex items-center justify-center w-4 h-4 rounded-full bg-white/20">
        <FiXCircle size={10} />
      </span>
      NOK
      <div className="absolute inset-0 rounded-full bg-white/0 group-hover/badge:bg-white/10 transition-colors duration-200" />
    </span>
  ) : (
    <span className="group/badge relative inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200 cursor-default">
      <span className="flex items-center justify-center w-4 h-4 rounded-full bg-white/20">
        <FiCheckCircle size={10} />
      </span>
      OK
      <div className="absolute inset-0 rounded-full bg-white/0 group-hover/badge:bg-white/10 transition-colors duration-200" />
    </span>
  );
}

function DaysCell({ value, row }: { value: number; row: any }) {
  const isAlert = row.original.alert;
  const alertDays = row.original.alert_days_without_report;

  return (
    <div className="flex items-center justify-center gap-2">
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-bold ${
          isAlert
            ? "bg-red-100 text-red-700"
            : "bg-gray-100 text-gray-700"
        }`}
      >
        <MdAccessTime size={14} />
        {value}
        <span className="text-xs font-normal opacity-70">dias</span>
      </span>
      {isAlert && (
        <span className="text-xs text-red-500 font-medium">
          (máx: {alertDays})
        </span>
      )}
    </div>
  );
}

function ClienteCell({ value }: { value: string }) {
  return (
    <div className="flex items-center justify-center">
      <span className="font-medium text-gray-800 truncate max-w-[250px]">
        {value}
      </span>
    </div>
  );
}

function DateCell({ value }: { value: string }) {
  const formattedDate = formatDate(value);
  return (
    <div className="flex items-center justify-center gap-1.5 text-gray-600">
      <FiCalendar size={14} className="text-gray-400" />
      <span>{formattedDate}</span>
    </div>
  );
}

// Componente de tabela geral (consolidada)
function GeneralTable({
  costumers,
}: {
  costumers: ListCostumerMonitoring[];
}) {
  const columns = useMemo(
    () => [
      {
        Header: "Relatório",
        accessor: "report_id" as const,
        Cell: ReportCell,
      },
      {
        Header: "Cliente",
        accessor: "costumer_name" as const,
        Cell: ClienteCell,
      },
      {
        Header: "Zona",
        accessor: "zone_name" as const,
        Cell: ({ value }: { value: string | null }) => (
          <div className="flex items-center justify-center">
            <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
              {value || "Sem Zona"}
            </span>
          </div>
        ),
      },
      {
        Header: "Último Relatório",
        accessor: "last_report" as const,
        Cell: DateCell,
      },
      {
        Header: "Dias sem Relatório",
        accessor: "days_without_report" as const,
        Cell: DaysCell,
      },
      {
        Header: "Situação",
        accessor: "alert" as const,
        Cell: SituacaoCell,
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: costumers });

  const totalOk = costumers.filter(c => !c.alert).length;
  const totalNok = costumers.filter(c => c.alert).length;
  const percentOk = costumers.length > 0 ? Math.round((totalOk / costumers.length) * 100) : 0;

  return (
    <div className="list-item-enter bg-white rounded-2xl mb-6 overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <FiGrid size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Visão Geral</h3>
              <p className="text-white/70 text-sm">{costumers.length} clientes monitorados</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Badge OK */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">
              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                <FiCheckCircle size={12} className="text-white" />
              </div>
              <span className="text-white font-semibold">{totalOk}</span>
              <span className="text-white/70 text-sm">OK</span>
            </div>
            {/* Badge NOK */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">
              <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                <FiXCircle size={12} className="text-white" />
              </div>
              <span className="text-white font-semibold">{totalNok}</span>
              <span className="text-white/70 text-sm">NOK</span>
            </div>
            {/* Porcentagem */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full">
              <span className="text-white/90 text-sm font-medium">{percentOk}% em dia</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="w-full overflow-x-auto">
        <table
          {...getTableProps()}
          className="min-w-full"
        >
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id} className="bg-gray-50/80">
                {headerGroup.headers.map(column => (
                  <th
                    {...column.getHeaderProps()}
                    className="px-6 py-4 text-xs font-bold text-gray-500 text-center uppercase tracking-wider border-b border-gray-200"
                    key={column.id}
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            {...getTableBodyProps()}
            className="divide-y divide-gray-100"
          >
            {rows.map((row, index) => {
              prepareRow(row);
              const isAlert = row.original.alert;
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  className={`transition-colors duration-150 ${
                    isAlert
                      ? "bg-red-50/50 hover:bg-red-50"
                      : index % 2 === 0
                      ? "bg-white hover:bg-gray-50"
                      : "bg-gray-50/30 hover:bg-gray-50"
                  }`}
                >
                  {row.cells.map(cell => (
                    <td
                      {...cell.getCellProps()}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-600"
                      key={cell.column.id}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Componente de tabela por zona
function ZoneTable({
  zone,
  costumers,
}: {
  zone: ZoneInfo | null;
  costumers: ListCostumerMonitoring[];
}) {
  const columns = useMemo(
    () => [
      {
        Header: "Relatório",
        accessor: "report_id" as const,
        Cell: ReportCell,
      },
      {
        Header: "Cliente",
        accessor: "costumer_name" as const,
        Cell: ClienteCell,
      },
      {
        Header: "Último Relatório",
        accessor: "last_report" as const,
        Cell: DateCell,
      },
      {
        Header: "Dias sem Relatório",
        accessor: "days_without_report" as const,
        Cell: DaysCell,
      },
      {
        Header: "Situação",
        accessor: "alert" as const,
        Cell: SituacaoCell,
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: costumers });

  const zoneName = zone ? zone.name : "Sem Zona";
  const totalOk = costumers.filter(c => !c.alert).length;
  const totalNok = costumers.filter(c => c.alert).length;
  const percentOk = costumers.length > 0 ? Math.round((totalOk / costumers.length) * 100) : 0;

  return (
    <div className="list-item-enter bg-white rounded-2xl mb-6 overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
      {/* Cabeçalho da zona */}
      <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <FiFileText size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{zoneName}</h3>
              <p className="text-white/70 text-sm">{costumers.length} clientes</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Badge OK */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">
              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                <FiCheckCircle size={12} className="text-white" />
              </div>
              <span className="text-white font-semibold">{totalOk}</span>
              <span className="text-white/70 text-sm">OK</span>
            </div>
            {/* Badge NOK */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">
              <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                <FiXCircle size={12} className="text-white" />
              </div>
              <span className="text-white font-semibold">{totalNok}</span>
              <span className="text-white/70 text-sm">NOK</span>
            </div>
            {/* Porcentagem */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full">
              <span className="text-white/90 text-sm font-medium">{percentOk}% em dia</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="w-full overflow-x-auto">
        <table
          {...getTableProps()}
          className="min-w-full"
        >
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id} className="bg-gray-50/80">
                {headerGroup.headers.map(column => (
                  <th
                    {...column.getHeaderProps()}
                    className="px-6 py-4 text-xs font-bold text-gray-500 text-center uppercase tracking-wider border-b border-gray-200"
                    key={column.id}
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            {...getTableBodyProps()}
            className="divide-y divide-gray-100"
          >
            {rows.map((row, index) => {
              prepareRow(row);
              const isAlert = row.original.alert;
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  className={`transition-colors duration-150 ${
                    isAlert
                      ? "bg-red-50/50 hover:bg-red-50"
                      : index % 2 === 0
                      ? "bg-white hover:bg-gray-50"
                      : "bg-gray-50/30 hover:bg-gray-50"
                  }`}
                >
                  {row.cells.map(cell => (
                    <td
                      {...cell.getCellProps()}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-600"
                      key={cell.column.id}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CostumersMonitoring() {
  const { hasPermission } = useAuth();

  // Determinar permissões e tipo padrão
  const canViewTechnicalVisit = hasPermission("tech-visit-reports:view-technical-visit");
  const canViewDelivery = hasPermission("tech-visit-reports:view-delivery");
  const hasBothPermissions = canViewTechnicalVisit && canViewDelivery;

  // Definir tipo padrão baseado nas permissões
  const getDefaultType = (): "technical_visit" | "delivery" => {
    if (canViewTechnicalVisit && !canViewDelivery) return "technical_visit";
    if (canViewDelivery && !canViewTechnicalVisit) return "delivery";
    return "technical_visit"; // Padrão se tiver ambas ou nenhuma
  };

  const [reportType, setReportType] = useState<"technical_visit" | "delivery">(getDefaultType());
  const [costumersByZone, setCostumersByZone] = useState<CostumersByZone[]>([]);
  const [allCostumers, setAllCostumers] = useState<ListCostumerMonitoring[]>([]);
  const [bigNumbersMonitoring, setBigNumbersMonitoring] =
    useState<BigNumbersMonitoring>();
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [tableScale, setTableScale] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>("general");

  const fetchMonitoringData = async (type: string) => {
    try {
      setDataLoading(true);
      const response = await api.post("/costumers/monitoring", { type });
      setCostumersByZone(response.data.costumersByZone);
      setBigNumbersMonitoring(response.data.bigNumbers);
      // Criar lista consolidada de todos os clientes
      const allCustomers = response.data.costumersByZone.reduce(
        (acc: ListCostumerMonitoring[], zoneGroup: CostumersByZone) => {
          return [...acc, ...zoneGroup.costumers];
        },
        []
      );
      // Ordenar por dias sem relatório (decrescente)
      allCustomers.sort((a: ListCostumerMonitoring, b: ListCostumerMonitoring) =>
        b.days_without_report - a.days_without_report
      );
      setAllCostumers(allCustomers);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao buscar dados de monitoramento:", error);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchMonitoringData(reportType);
      setLoading(false);
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchMonitoringData(reportType);
    }
  }, [reportType]);

  useEffect(() => {
    function handleResize() {
      const screenWidth = window.innerWidth;
      if (screenWidth < 640) {
        // Small mobile devices
        setTableScale(0.5);
      } else if (screenWidth < 768) {
        // Larger mobile devices
        setTableScale(0.75);
      } else {
        // Tablets and desktops
        setTableScale(1);
      }
    }

    window.addEventListener("resize", handleResize);
    handleResize(); // Call once on mount

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4">
        <PageTitle
          title="Monitoramento de Clientes"
          backButton
          backPath="/tech-visit-reports"
        />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0085BD]" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4">
      <PageTitle
        title="Monitoramento de Clientes"
        backButton
        backPath="/tech-visit-reports"
      />

      {/* Toggle de Tipo de Relatório - Mostrar apenas se tiver ambas permissões */}
      {hasBothPermissions && (
      <div className="flex justify-start items-center gap-4 mb-6">
        <div className="inline-flex items-center bg-white rounded-full shadow-md border border-gray-200 p-1">
          <button
            onClick={() => setReportType("technical_visit")}
            disabled={dataLoading}
            className={`px-6 py-2 rounded-full font-medium text-sm transition-all duration-300 ${
              reportType === "technical_visit"
                ? "bg-gradient-to-r from-[#0085BD] to-[#006a96] text-white shadow-lg"
                : "text-gray-600 hover:text-gray-800"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Visita Técnica
          </button>
          <button
            onClick={() => setReportType("delivery")}
            disabled={dataLoading}
            className={`px-6 py-2 rounded-full font-medium text-sm transition-all duration-300 ${
              reportType === "delivery"
                ? "bg-gradient-to-r from-[#0085BD] to-[#006a96] text-white shadow-lg"
                : "text-gray-600 hover:text-gray-800"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Entrega
          </button>
        </div>
        {dataLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0085BD]/30 border-t-[#0085BD]" />
            <span>Atualizando...</span>
          </div>
        )}
      </div>
      )}

      <div
        style={{
          transform: `scale(${tableScale})`,
          transformOrigin: "left top",
          width: `${100 / tableScale}%`,
        }}
      >
        {/* Big Numbers */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 mt-3 transition-opacity duration-300 ${
          dataLoading ? 'opacity-50' : 'opacity-100'
        }`}>
          {/* Card Total OK */}
          <div className="group relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-green-500/10 to-emerald-500/5 rounded-bl-full" />
            <div className="relative flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                <FiCheckCircle size={28} className="text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  {bigNumbersMonitoring?.total_false || 0}
                </p>
                <p className="text-sm text-gray-500 font-medium">Clientes em dia</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-1.5 text-green-600">
              <MdTrendingUp size={16} />
              <span className="text-xs font-medium">Status OK</span>
            </div>
          </div>

          {/* Card Total NOK */}
          <div className="group relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-red-500/10 to-rose-500/5 rounded-bl-full" />
            <div className="relative flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/30 group-hover:scale-110 transition-transform duration-300">
                <FiXCircle size={28} className="text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
                  {bigNumbersMonitoring?.total_true || 0}
                </p>
                <p className="text-sm text-gray-500 font-medium">Precisam atenção</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-1.5 text-red-600">
              <FiAlertTriangle size={16} />
              <span className="text-xs font-medium">Status NOK</span>
            </div>
          </div>

          {/* Card Emergências com animação piscante */}
          <div className={`group relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden ${
            allCostumers.filter(c => c.emergency).length > 0 ? 'animate-pulse-border-emergency' : ''
          }`}>
            <style>
              {`
                @keyframes pulse-border-emergency {
                  0%, 100% {
                    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4), 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                  }
                  50% {
                    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0), 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                  }
                }
                .animate-pulse-border-emergency {
                  animation: pulse-border-emergency 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                }
              `}
            </style>
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-500/10 to-amber-500/5 rounded-bl-full" />
            <div className="relative flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform duration-300">
                <FiAlertTriangle size={28} className="text-white" />
              </div>
              <div>
                <p className={`text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent ${
                  allCostumers.filter(c => c.emergency).length > 0 ? 'animate-pulse' : ''
                }`}>
                  {allCostumers.filter(c => c.emergency).length}
                </p>
                <p className="text-sm text-gray-500 font-medium">Emergências</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-1.5 text-orange-600">
              <FiAlertTriangle size={16} />
              <span className="text-xs font-medium">Prioridade</span>
            </div>
          </div>

          {/* Card Taxa de Conformidade */}
          <div className="group relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-500/10 to-indigo-500/5 rounded-bl-full" />
            <div className="relative flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                <MdTrendingUp size={28} className="text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  {((bigNumbersMonitoring?.total_false || 0) + (bigNumbersMonitoring?.total_true || 0)) > 0
                    ? Math.round(((bigNumbersMonitoring?.total_false || 0) / ((bigNumbersMonitoring?.total_false || 0) + (bigNumbersMonitoring?.total_true || 0))) * 100)
                    : 0}%
                </p>
                <p className="text-sm text-gray-500 font-medium">Taxa de conformidade</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-1.5 text-purple-600">
              <MdTrendingUp size={16} />
              <span className="text-xs font-medium">Performance</span>
            </div>
          </div>
        </div>

        {/* Toggle de Visualização com 3 opções */}
        <div className={`mb-6 transition-opacity duration-300 ${
          dataLoading ? 'opacity-50' : 'opacity-100'
        }`}>
          <div className="relative bg-gray-100 rounded-full p-1 h-[48px] flex items-center max-w-[480px]">
            {/* Indicador deslizante */}
            <div
              className="absolute h-[40px] bg-[#0085BD] rounded-full shadow-md transition-all duration-300 ease-in-out"
              style={{
                width: "calc(33.333% - 4px)",
                left: viewMode === "general" ? "4px" : viewMode === "byZone" ? "calc(33.333% + 2px)" : "calc(66.666% + 0px)",
              }}
            />
            <button
              type="button"
              onClick={() => setViewMode("general")}
              className={`relative z-10 flex-1 h-[40px] rounded-full flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold transition-colors duration-300 ${
                viewMode === "general" ? "text-white" : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <FiGrid size={16} />
              <span className="hidden sm:inline">Geral</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode("byZone")}
              className={`relative z-10 flex-1 h-[40px] rounded-full flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold transition-colors duration-300 ${
                viewMode === "byZone" ? "text-white" : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <FiLayers size={16} />
              <span className="hidden sm:inline">Por Zona</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode("emergency")}
              className={`relative z-10 flex-1 h-[40px] rounded-full flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold transition-colors duration-300 ${
                viewMode === "emergency" ? "text-white" : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <FiAlertTriangle size={16} />
              <span className="hidden sm:inline">Em Emergência</span>
            </button>
          </div>
        </div>

        {/* Tabelas */}
        <div className={`transition-opacity duration-300 ${
          dataLoading ? 'opacity-50' : 'opacity-100'
        }`}>
          {viewMode === "general" ? (
            <GeneralTable costumers={allCostumers} />
          ) : viewMode === "byZone" ? (
            costumersByZone.map(zoneGroup => (
              <ZoneTable
                key={zoneGroup.zone?.id || "no-zone"}
                zone={zoneGroup.zone}
                costumers={zoneGroup.costumers}
              />
            ))
          ) : (
            <GeneralTable costumers={allCostumers.filter(c => c.emergency === true)} />
          )}
        </div>
      </div>
    </div>
  );
}
